import { useRef } from "react";
import { useStore } from "react-redux";

import { getAnalysisLegendConfig } from "@/api/analysis/analysisApi";
import { usePutAnalysisLegendConfig } from "@/api/analysis/useAnalysisAPI";
import { getAnalysisConfig } from "@/api/configuration/configurationApi";
import { MESSAGE } from "@/constants/message";
import type { RootState } from "@/store";
import type { Dataset, StyleConfig } from "@/types/dataset";
import type {
  AnalysisDatasetLegendConfig,
  DatasetConfigResponse,
  LegendAttributeRef
} from "@/types/project";
import {
  embedLegendConfigImages,
  mapDatasetToAnalysisLegendConfig,
  mapLegendToStyleConfig,
  resolveMarkerAssetUrl
} from "@/utils/configurationMapper";
import { debounce } from "@/utils/debounce";

import useConfigurationMappers from "../project/useConfigurationMapper";
import useToast, { EToastType } from "../ui/useToast";
import { useAppFeatures } from "../useAppFeatures";

/** Edits that fire repeatedly (slider drags, typed steps) wait this long before
 * the config is saved. */
const LEGEND_SAVE_DEBOUNCE_MS = 500;

/** One result dataset's restored legend. The column stays unresolved: a result
 * dataset's attributes only exist once its columns have streamed. */
export interface RestoredAnalysisLegend {
  style: StyleConfig;
  colorBased?: LegendAttributeRef | null;
}

/** Restored legends, keyed by result dataset id. */
export type AnalysisLegendStyles = Record<string, RestoredAnalysisLegend>;

/**
 * Get & save the legend config of a spatial analysis RESULT (ORB-3341 / ORB-3513):
 * every panel Save replaces the whole list, and opening an analysis restores it —
 * falling back to the pre-analysis config while nothing has been saved yet.
 */
export const useAnalysisLegendConfig = () => {
  const store = useStore<RootState>();
  const { showToast } = useToast();
  const { isCustomMapVisualizationLegendEnabled } = useAppFeatures();
  const { uploadPendingLegendImages } = useConfigurationMappers();
  const { mutateAsync: putLegendConfig } = usePutAnalysisLegendConfig();
  /** Payload of the last successful save, so an unchanged one is not sent again. */
  const lastSavedPayloadRef = useRef<string | null>(null);

  /**
   * Persists every result dataset's legend config. Reads the store directly so it
   * sees what the panel just committed, and no-ops outside the Analysis Result
   * panel. Resolves `false` only when an attempted save failed.
   */
  const persistAnalysisLegendConfig = async (): Promise<boolean> => {
    if (!isCustomMapVisualizationLegendEnabled) return true;

    const { spatial, ui } = store.getState();
    const analysisId = spatial.latestAnalysisId;
    if (!analysisId || !ui.showAnalysisResultPopup) return true;

    // Upload marker images that are still data URLs, so the payload can carry
    // their asset ids.
    let assetIdByImageUrl = new Map<string, string>();
    try {
      assetIdByImageUrl = await uploadPendingLegendImages(
        store.getState().dataset.datasets
      );
    } catch {
      showToast({
        type: EToastType.ERROR,
        message: MESSAGE.CUSTOM_LEGEND_SAVE_LEGEND_CONFIG_FAILED_AFTER_ANALYSIS
      });
      return false;
    }

    const { datasets, visualizationOrder } = store.getState().dataset;
    const datasetConfigs = visualizationOrder
      .filter((datasetId) => datasets[datasetId])
      .map((datasetId) =>
        mapDatasetToAnalysisLegendConfig(
          datasetId,
          datasets[datasetId] as Dataset,
          assetIdByImageUrl
        )
      );
    if (!datasetConfigs.length) return true;

    // The payload carries every dataset, so an identical one means nothing was
    // edited since the last save — e.g. Reset on a config already at its default.
    const payload = { analysisId, datasets: datasetConfigs };
    const serialized = JSON.stringify(payload);
    if (serialized === lastSavedPayloadRef.current) return true;

    try {
      await putLegendConfig({
        analysisId,
        payload: { datasets: datasetConfigs }
      });
      // Only a saved payload is remembered, so a failed save can be retried.
      lastSavedPayloadRef.current = serialized;
      return true;
    } catch {
      showToast({
        type: EToastType.ERROR,
        message: MESSAGE.CUSTOM_LEGEND_SAVE_LEGEND_CONFIG_FAILED_AFTER_ANALYSIS
      });
      return false;
    }
  };

  /**
   * Same save, delayed until the edits stop: a slider drag or a typed step fires
   * many changes, and only the value the user settles on needs saving. Created
   * once, and it reads the store when it runs, so it never sends a stale config.
   */
  const persistAnalysisLegendConfigDebounced = useRef(
    debounce(() => {
      persistAnalysisLegendConfig();
    }, LEGEND_SAVE_DEBOUNCE_MS)
  ).current;

  /** One entry's legend, with marker images embedded for the map canvas. */
  const toStyleConfig = async (
    entry: AnalysisDatasetLegendConfig | DatasetConfigResponse
  ): Promise<StyleConfig | undefined> =>
    await embedLegendConfigImages(
      mapLegendToStyleConfig(entry),
      resolveMarkerAssetUrl
    );

  /** Legend styles to apply to the result layers, keyed by dataset id; empty when
   * there is nothing to restore. */
  const fetchAnalysisLegendStyles = async (
    analysisId: string
  ): Promise<AnalysisLegendStyles> => {
    if (!isCustomMapVisualizationLegendEnabled || !analysisId) return {};

    const saved = await getAnalysisLegendConfig(analysisId).catch(() => null);

    if (saved?.length) {
      const entries = await Promise.all(
        saved.map(async (item) => {
          const legend = await toStyleConfig(item);
          if (!legend) return null;
          return [
            item.id,
            {
              style: {
                ...legend,
                // Saved post-analysis, so these are authoritative for the result view.
                ...(item.color_scale ? { colorFamily: item.color_scale } : {}),
                ...(item.color_steps ? { colorSteps: item.color_steps } : {}),
                ...(typeof item.opacity === "number"
                  ? { opacity: item.opacity }
                  : {})
              },
              colorBased: item.color_based
            }
          ] as const;
        })
      );
      return Object.fromEntries(
        entries.filter(Boolean) as [string, RestoredAnalysisLegend][]
      );
    }

    // Nothing saved yet: seed the legend (only) from the pre-analysis config, so
    // the result layers keep their generated palette.
    const config = await getAnalysisConfig(analysisId).catch(() => null);
    if (!config?.configuration) return {};

    let configDatasets: DatasetConfigResponse[] = [];
    try {
      configDatasets = JSON.parse(config.configuration)?.datasets ?? [];
    } catch {
      return {};
    }

    const entries = await Promise.all(
      configDatasets.map(async (dataset) => {
        const legend = await toStyleConfig(dataset);
        // Legend only — the pre-analysis column belongs to the input dataset.
        return legend ? ([dataset.id, { style: legend }] as const) : null;
      })
    );
    return Object.fromEntries(
      entries.filter(Boolean) as [string, RestoredAnalysisLegend][]
    );
  };

  return {
    persistAnalysisLegendConfig,
    persistAnalysisLegendConfigDebounced,
    fetchAnalysisLegendStyles
  };
};

export default useAnalysisLegendConfig;
